import { Dialog, DialogClose, DialogContent, DialogOverlay } from '@weee-fe/kunlun';
import { SyntheticEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import closeImg from '@/assets/images/popup_bottom_close.png';
import callAppFunction from '@/utils/bridge';
import { fixedBody, isSayWeeeApp, looseBody } from '@/utils/tool';

import { twMerge } from 'tailwind-merge';
import { WeeeIcon } from '../Icon';
import closest from './_utils/close';
import confirm from './confirm';
import withLazy, { LazyProps } from './withLazy';

type BasicDialogProps = {
  className?: string;
  visible?: boolean;
  closable?: boolean;
  onClose?: (e?) => void;
  closeApp?: boolean;
  bottomCloseIcon?: boolean;
  transparent?: boolean;
  children: React.ReactNode;
  title?: React.ReactNode | string;
  maskClosable?: boolean;
  maskStyle?: React.CSSProperties;
  dialogContentStyle?: React.CSSProperties;
  closableName?: string;
  closableIconName?: string;
  [key: string]: any;
};

type DialogDefaultProps = Partial<BasicDialogProps>;

const PREFIX_CLASSNAME = 'kunlun-dialog-content-wrapper';

const Modal = props => {
  const {
    visible = false,
    className,
    title,
    closable = false,
    maskClosable = true,
    maskStyle,
    dialogContentStyle,
    closableName,
    closableIconName,
    ...rest
  } = props;

  const handleClose = (e: SyntheticEvent) => {
    e.stopPropagation();
    props?.onClose?.();
  };

  const handleMaskClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (maskClosable) {
      handleClose(e);
    }
  };

  return (
    <Dialog open={visible}>
      <DialogOverlay style={maskStyle} className="backdrop-blur-none" onClick={handleMaskClick} />
      <DialogContent
        {...rest}
        style={dialogContentStyle}
        className={twMerge(
          'bg-surface-100-bg box-border w-[calc(100vw-40px)] max-w-[750px] text-center text-surface-100-fg-minor enki-body-base !rounded-600 p-500 flex justify-center items-center overflow-hidden outline-none shadow-none',
          className
        )}>
        <div className="relative w-full">
          {closable && (
            <div
              className={twMerge(
                'absolute z-[1002] w-[20px] h-[20px] top-0 right-0 flex items-center justify-center',
                closableName
              )}
              onClick={handleClose}>
              <WeeeIcon type="iconclose" className={twMerge('text-surface-100-fg-minor', closableIconName)} />
            </div>
          )}
          {title && typeof title === 'string' ? (
            <div
              className={twMerge(
                'm-0 pt-200 pb-400 enki-heading-xl text-surface-100-fg-default text-center',
                `${PREFIX_CLASSNAME}_title`
              )}>
              {title}
            </div>
          ) : (
            title
          )}
          <div className={`${PREFIX_CLASSNAME} w-full`}>{props?.children}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CustomDialog(props: DialogDefaultProps) {
  const { className, bottomCloseIcon = false, closeApp = true } = props;
  const [loaded, setLoaded] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(props.visible);
  useEffect(() => {
    if (!visible) return;
    setLoaded(true);
  }, [visible]);

  useEffect(() => {
    setVisible(props.visible);
  }, [props.visible]);

  const handleClose = () => {
    setVisible(false);
    props?.onClose?.();
    if (isSayWeeeApp() && closeApp) {
      try {
        callAppFunction('closeWindow');
      } catch (error) {
        console.log('callAppFunction:closeWindow', error);
      }
    }
  };

  const renderBottomCloseIcon = () => {
    if (bottomCloseIcon && loaded) {
      const _domWrapper = document.querySelector(`.${PREFIX_CLASSNAME}`);
      //兼容dom不存在时会报错
      if (_domWrapper) {
        return createPortal(
          <div className="w-fit mt-400 mx-auto text-center" onClick={handleClose}>
            <img className="w-1000 h-1000" src={closeImg} alt="close" />
          </div>,
          _domWrapper
        );
      }
    }
  };

  const closable = !bottomCloseIcon && props.closable;

  if (!visible) return null;

  return (
    <>
      {renderBottomCloseIcon()}
      <Modal {...props} visible={visible} closable={closable} className={className} />
    </>
  );
}

CustomDialog.confirm = confirm;

export { closest, withLazy };

export type LazyType = LazyProps;
